import { isBrowser } from '@mc/base';
import NottaLogo from '@/images/Logo-Notta-Dark.png';
import { ICMSLink } from './type';

export const BrandLogo = NottaLogo;
export const getPrefixCls = (type: string) => {
  return `notta-${type}`;
};

/**
 * 日文导航栏的高度
 */
export const NAV_HEIGHT_JP = 69;

type Options = {
  format?: string;
  quality?: number;
  blur: [number?, number?];
};

export const processCdnUrl = (url: string, options: Options = { format: 'webp', quality: 100, blur: [0, 10] }) => {
  const { format, quality, blur } = options;

  return `${url}/m/filters:format(${format}):quality(${quality}):blur(${blur[0]})`;
};

export const blogPathArr = [
  '/blog/mp4-to-text',
  '/blog/best-voice-recorder',
  '/blog/mac-transcription',
  '/blog/10-best-google-chrome-extensions',
  '/blog/youtube-transcription',
];

export const getHotBlogParams = () => {
  if (typeof window === 'undefined') return null;
  const isHotBlog = blogPathArr.includes(window.location.pathname);
  return isHotBlog ? window.location.pathname.replace('/blog/', '') : null;
};

export const getABTestLoginSignUpUrl = (type: 'login' | 'signup') => {
  if (typeof window === 'undefined') return null;
  const origin = window.location.origin;

  let currentOrigin = 'https://app.notta.ai';
  if (origin.includes('dev')) {
    currentOrigin = 'https://dev-c0ee1fac76df136a.notta.ai';
  } else if (origin.includes('test')) {
    currentOrigin = 'https://test-app-12154205867b06a3.notta.ai';
  }

  return getHotBlogParams()
    ? `${currentOrigin}/${type}?language=ja&from=official&blog=${getHotBlogParams()}`
    : `${currentOrigin}/${type}?language=ja&from=official`;
};

// 加载脚本
export const loadScript = (src: string, id: string, className?: string) => {
  return new Promise<void>((resolve, reject) => {
    const isExitScript = document.getElementById(id);
    if (isExitScript) return;

    const script = document.createElement('script');
    script.id = id;
    script.classList.add(className);
    script.defer = true;
    script.src = src;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      reject();
    };
    document.body.appendChild(script);
  });
};

export const addLeadingZero = (number) => {
  return number > 0 && number < 10 ? `0${number}` : `${number}`;
};

export const randomIntFromInterval = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const getNumberFromRange = (min: number, max: number, n: number) => {
  if (n > max) {
    return max;
  } else if (n < min) {
    return min;
  } else {
    return n;
  }
};

export const loadFont = (fontFamily, fontStyle, fontWeight, url) => {
  const font = new FontFace(fontFamily, `url(${url})`, {
    style: fontStyle,
    weight: fontWeight,
  });

  font
    .load()
    .then((loadedFont) => {
      if (isBrowser()) {
        document.fonts?.add(loadedFont);
      }
    })
    .catch(() => {});
};

export type SIGN_TYPE = 'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export interface IResizeSchema {
  base?: any;
  sm?: any;
  md?: any;
  lg?: any;
  xl?: any;
  '2xl'?: any;
}

export const WINDOW_SIZE = {
  base: 575,
  sm: 767,
  md: 991,
  lg: 1200,
  xl: 1600,
};

export const calcWindowSizeSign = (windowWidth) => {
  if (windowWidth <= WINDOW_SIZE.base) {
    return 'base';
  } else if (windowWidth <= WINDOW_SIZE.sm) {
    return 'sm';
  } else if (windowWidth <= WINDOW_SIZE.md) {
    return 'md';
  } else if (windowWidth <= WINDOW_SIZE.lg) {
    return 'lg';
  } else if (windowWidth <= WINDOW_SIZE.xl) {
    return 'xl';
  } else {
    return '2xl';
  }
};

export const getValueBySignSchema = (sign: SIGN_TYPE, schema: IResizeSchema) => {
  const SIGN_ARR: SIGN_TYPE[] = ['base', 'sm', 'md', 'lg', 'xl', '2xl'];

  const value = schema[sign];

  if (value) {
    return value;
  }

  const startIndex = SIGN_ARR.indexOf(sign) - 1;

  for (let i = startIndex; i >= 0; i--) {
    const tempSign = SIGN_ARR[i];
    if (schema[tempSign]) {
      return schema[tempSign];
    }
  }

  return '';
};

/**
 * 获取最大距离没有重复元素的数组
 * @param arr 资源数组
 * @param n 需要取多少个元素
 * @returns 最大距离没有重复元素的数组
 */
export const largestDistanceNoRepeatArr = (arr: Array<any>, n: number): Array<any> => {
  const arrLen = arr.length;
  if (arrLen >= n) {
    return arr;
  }
  const repeatCount = Math.floor(n / arrLen);
  const remainCount = n % arrLen;
  let leftPos = 0,
    rightPos = arrLen - 1;
  let isLeftPosLastMove = false;
  while (rightPos - leftPos + 1 > remainCount) {
    if (!isLeftPosLastMove) {
      leftPos++;
      isLeftPosLastMove = true;
    } else {
      rightPos--;
      isLeftPosLastMove = false;
    }
  }
  const suppleArr = arr.slice(leftPos, rightPos + 1);
  const res = [];
  for (let i = 0; i < repeatCount; ++i) {
    res.push(...arr);
  }
  res.push(...suppleArr);
  return res;
};

/**
 * 替换<wbr>和<br>标签为空字符串
 * @param inputString 待处理的字符串
 * @returns 把<wbr/>和<br/>替换成空格后的字符串
 */
export const removeWbrAndBr = (inputString) => {
  if (typeof inputString !== 'string') return '';
  // 使用正则表达式替换<wbr>和<br>标签为空字符串
  const outputString = inputString.replace(/<wbr\s*\/?>/gi, '').replace(/<br\s*\/?>/gi, '');

  return outputString;
};

/**
 * 根据CMS link获取URL
 * @param link CMS link 类型
 * @returns 能直接使用的url
 */
export const getRealLinkByCMSLink = (link: ICMSLink) => {
  if (link.linktype === 'url') {
    return link.url;
  }
  return `/${link.cached_url}`;
};

export const getPriceSuffix = (type) => {
  if (type === 'en') {
    return 'USD';
  }
  return '円';
};

declare global {
  interface Window {
    dataLayer: any;
  }
}
type GA4Event = Record<string, any> & { event?: string };
/**
 * 业务数据分析。
 * @param eventParams 事件参数
 */
export const GaDataLayer = (eventParams: GA4Event): void => {
  window.dataLayer.push(eventParams);
};
